import { browserLogger } from "@@/settings";
import type { FormId } from "@@/shared/form/form-and-field-id";
import { useFormId } from "@@/shared/form/form-id.context";
import type { DeepKeysOf } from "@towni/common";
import { assertNever } from "@towni/common";
import { useMountEffect } from "../use-mount-effect";
import { useForm3Store } from "./form3.zustand-store";
import { getFormValueAtPath } from "./get-value-at-path";

const createForm3DebugComponent = <
    FormData extends Record<string, unknown>,
>() => {
    const Form3Debug = (props: {
        formId?: FormId;
        /** defaults to `console` */
        mode?: "console" | "render";
        className?: string;
        path?: DeepKeysOf<FormData>;
    }) => {
        const mode = props.mode ?? "render";
        const _formIdFromContext = useFormId({ doNotThrow: true });
        const formId = props.formId || _formIdFromContext;
        if (!formId)
            throw new Error(
                "Form id not set and no form id provider is available",
            );
        const formStore = useForm3Store();
        const form = formStore.getForm(formId);
        useMountEffect(() => {
            browserLogger.info(
                `Form3 Debug Component initialized with formId: ${formId}`,
            );
            browserLogger.info(
                "Available forms: ",
                Object.keys(formStore.forms).join(", "),
            );
            browserLogger.info(
                "FormId in context: ",
                _formIdFromContext ?? "none",
            );
            browserLogger.info("FormId as prop: ", props.formId ?? "none");
        });
        if (!form) return null;
        const debugOutput = props.path
            ? getFormValueAtPath(form.data, props.path)
            : form.data;
        switch (mode) {
            case "console":
                browserLogger.log("Form debug output: ", debugOutput);
                return null;
            case "render":
                return (
                    <div
                        className={props.className}
                        css={{
                            color: "rgba(0,0,0,0.8)",
                            backgroundColor: "rgba(0,0,0,0.1)",
                            borderRadius: 10,
                            padding: 20,
                            overflowX: "hidden",
                            overflowY: "visible",
                            minHeight: 150,
                        }}>
                        <span>DEBUG</span>
                        <pre>
                            <code
                                css={{
                                    userSelect: "text",
                                    whiteSpace: "pre-wrap",
                                }}>
                                {props.path
                                    ? `${props.path.toLocaleString()}: ${JSON.stringify(debugOutput, null, 2)}`
                                    : JSON.stringify(debugOutput, null, 2)}
                            </code>
                        </pre>
                    </div>
                );
            default:
                assertNever(mode);
        }
    };
    return Form3Debug;
};

export { createForm3DebugComponent };
